import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { shuffle, standardDeck, type Card } from '@campfire/cards';
import { bestHand, compareScores, type HandValue } from './hands.ts';

/**
 * Texas Hold'em (FR-HOLDEM-*). With betting off the app only deals: it runs the button, blinds, dealing
 * and the showdown, and the dealer (button) deals each street. With betting on (default) it also keeps
 * stacks, posts blinds, runs no-limit betting rounds, builds side pots and pays them out.
 */
export type Street = 'preflop' | 'flop' | 'turn' | 'river' | 'showdown';

export interface Positions {
  readonly button: Seat;
  readonly smallBlind: Seat;
  readonly bigBlind: Seat;
  /** First to act this street: left of the big blind preflop, left of the button after. */
  readonly firstToAct: Seat;
}

export interface ShowdownEntry {
  readonly seat: Seat;
  readonly hand: HandValue;
}

export interface PotInfo {
  readonly amount: number;
  /** Seats still in the hand that can win this pot. */
  readonly eligible: readonly Seat[];
}

export interface HandResult {
  /** Seats that won something (betting on) or the best hand(s) (betting off). */
  readonly winners: readonly Seat[];
  /** Chips each seat won this hand (betting on only). */
  readonly payouts?: readonly number[];
  /** The pots that were paid out, main first (betting on only). */
  readonly pots?: readonly PotInfo[];
  /** Empty when everyone else folded (no cards shown). */
  readonly shown: readonly ShowdownEntry[];
}

export type HoldemEvent =
  | { readonly kind: 'deal'; readonly hand: number; readonly button: Seat }
  | { readonly kind: 'street'; readonly street: Street }
  | { readonly kind: 'fold'; readonly seat: Seat }
  | { readonly kind: 'check'; readonly seat: Seat }
  | { readonly kind: 'call'; readonly seat: Seat; readonly amount: number }
  /** A bet or raise to `to` chips this street; `allIn` when it used the last chips. */
  | { readonly kind: 'raise'; readonly seat: Seat; readonly to: number; readonly allIn: boolean }
  | { readonly kind: 'leave'; readonly seat: Seat }
  | { readonly kind: 'won'; readonly winners: readonly Seat[]; readonly byFold: boolean };

export interface HoldemOptions {
  readonly betting: boolean;
  readonly stack: number;
  readonly bigBlind: number;
}

export interface HoldemState {
  readonly options: HoldemOptions;
  /** Still sitting at the table; players who leave are skipped by the button and blinds. */
  readonly seated: readonly boolean[];
  readonly button: Seat;
  readonly handNo: number;
  readonly deck: readonly Card[];
  readonly hole: readonly (readonly Card[])[];
  readonly folded: readonly boolean[];
  readonly board: readonly Card[];
  readonly burned: readonly Card[];
  readonly street: Street;
  readonly hand: HandResult | null;
  readonly last: HoldemEvent | null;
  readonly result: Outcome | null;
  /** Chips behind each seat (betting on). Chips of a player who leaves stay here, out of play. */
  readonly stacks: readonly number[];
  /** Chips each seat has put in this hand, all streets. */
  readonly committed: readonly number[];
  /** Chips each seat has put in on the current street. */
  readonly bets: readonly number[];
  /** Highest bet on the current street. */
  readonly currentBet: number;
  /** Size of the last full raise (at least the big blind): the next raise must add this much. */
  readonly minRaise: number;
  /** Has acted since the last full raise; such a player cannot re-raise a short all-in. */
  readonly acted: readonly boolean[];
  /** Whose turn to bet; null when nobody is betting (betting off, showdown). */
  readonly toAct: Seat | null;
}

export type HoldemAction =
  | { readonly type: 'next' }
  | { readonly type: 'fold' }
  | { readonly type: 'leave' }
  | { readonly type: 'check' }
  | { readonly type: 'call' }
  /** Bet or raise so your total bet this street is `to`. */
  | { readonly type: 'bet'; readonly to: number }
  | { readonly type: 'allin' };

export interface HoldemView {
  readonly betting: boolean;
  readonly bigBlind: number;
  readonly stacks: readonly number[];
  /** Chips each seat has in on this street. */
  readonly bets: readonly number[];
  readonly committed: readonly number[];
  /** Everything in the middle: all pots plus this street's bets. */
  readonly pot: number;
  readonly pots: readonly PotInfo[];
  readonly toAct: Seat | null;
  /** What the viewer must add to call (0 when not their turn). */
  readonly toCall: number;
  /** Smallest legal bet or raise, as a total for the street (0 when not the viewer's turn or no raise is possible). */
  readonly minRaiseTo: number;
  /** Most the viewer can bet, as a total for the street. */
  readonly maxTo: number;
  readonly currentBet: number;
  readonly you: Seat | null;
  readonly hole: readonly Card[] | null;
  /** Everyone's hole cards that are face up (showdown, not folded). */
  readonly shown: readonly (readonly Card[] | null)[];
  readonly board: readonly Card[];
  readonly street: Street;
  readonly handNo: number;
  readonly positions: Positions;
  readonly seated: readonly boolean[];
  readonly folded: readonly boolean[];
  /** Players still in this hand. */
  readonly inHand: readonly boolean[];
  readonly hand: HandResult | null;
  readonly last: HoldemEvent | null;
  readonly result: Outcome | null;
}

/** Next seated player clockwise from `from` (exclusive). */
function nextSeated(seated: readonly boolean[], from: Seat): Seat {
  for (let k = 1; k <= seated.length; k++) {
    const s = (from + k) % seated.length;
    if (seated[s]) return s;
  }
  return from;
}

/**
 * Standard positions: small blind left of the button, big blind left of that. Heads-up the button
 * posts the small blind and acts first before the flop, last after it.
 */
export function positions(s: Pick<HoldemState, 'seated' | 'button' | 'street' | 'folded'>): Positions {
  const count = s.seated.filter(Boolean).length;
  const smallBlind = count === 2 ? s.button : nextSeated(s.seated, s.button);
  const bigBlind = nextSeated(s.seated, smallBlind);
  const live = s.seated.map((x, i) => x && !s.folded[i]);
  const firstLive = (from: Seat) => {
    for (let k = 1; k <= live.length; k++) {
      const seat = (from + k) % live.length;
      if (live[seat]) return seat;
    }
    return from;
  };
  const firstToAct = s.street === 'preflop' ? firstLive(bigBlind) : firstLive(s.button);
  return { button: s.button, smallBlind, bigBlind, firstToAct };
}

const inHand = (s: HoldemState) => s.seated.map((x, i) => x && !s.folded[i] && (s.hole[i]?.length ?? 0) > 0);

/** Shuffle and deal two cards each, one at a time starting left of the button. */
type HandBase = Omit<HoldemState, 'deck' | 'hole' | 'folded' | 'board' | 'burned' | 'street' | 'hand' | 'committed' | 'bets' | 'currentBet' | 'minRaise' | 'acted' | 'toAct'>;

function dealHand(s: HandBase, deck: readonly Card[]): HoldemState {
  const n = s.seated.length;
  const hole: Card[][] = Array.from({ length: n }, () => []);
  let d = [...deck];
  for (let round = 0; round < 2; round++) {
    let seat = s.button;
    for (let k = 0; k < n; k++) {
      seat = nextSeated(s.seated, seat);
      if ((hole[seat] as Card[]).length === round) (hole[seat] as Card[]).push(d.shift() as Card);
    }
  }
  const zeros = s.seated.map(() => 0);
  const dealt: HoldemState = {
    ...s,
    deck: d,
    hole,
    folded: s.seated.map(() => false),
    board: [],
    burned: [],
    street: 'preflop',
    hand: null,
    committed: zeros,
    bets: zeros,
    currentBet: 0,
    minRaise: s.options.bigBlind,
    acted: s.seated.map(() => false),
    toAct: null,
  };
  return s.options.betting ? postBlinds(dealt) : dealt;
}

const set = <T>(arr: readonly T[], i: number, v: T): T[] => arr.map((x, k) => (k === i ? v : x));
const sum = (a: readonly number[]) => a.reduce((x, y) => x + y, 0);

/** Moves chips from a seat's stack into the current street's bet. */
function put(s: HoldemState, seat: Seat, amount: number): HoldemState {
  return {
    ...s,
    stacks: set(s.stacks, seat, (s.stacks[seat] as number) - amount),
    bets: set(s.bets, seat, (s.bets[seat] as number) + amount),
    committed: set(s.committed, seat, (s.committed[seat] as number) + amount),
  };
}

/** The small and big blind go in automatically (a short stack posts what it has), then betting starts. */
function postBlinds(s: HoldemState): HoldemState {
  const p = positions(s);
  const big = s.options.bigBlind;
  const post = (t: HoldemState, seat: Seat, blind: number) => put(t, seat, Math.min(blind, t.stacks[seat] as number));
  const t = post(post(s, p.smallBlind, Math.floor(big / 2)), p.bigBlind, big);
  return settle({ ...t, currentBet: Math.max(...t.bets) }, p.bigBlind);
}

const canBet = (s: HoldemState, i: Seat) => inHand(s)[i] === true && (s.stacks[i] as number) > 0;

/** Still owes the table an action: facing a bet, or has not yet had a chance while others can still bet. */
function pending(s: HoldemState, i: Seat): boolean {
  if (!canBet(s, i)) return false;
  if ((s.bets[i] as number) < s.currentBet) return true;
  return !s.acted[i] && s.seated.filter((_, k) => canBet(s, k)).length >= 2;
}

/** Pots by all-in level: each level's chips can be won only by live players who put in that much. */
export function sidePots(committed: readonly number[], live: readonly boolean[]): PotInfo[] {
  const levels = [...new Set(committed.filter((c, i) => live[i]))].sort((a, b) => a - b);
  const pots: PotInfo[] = [];
  let prev = 0;
  levels.forEach((level, idx) => {
    const top = idx === levels.length - 1;
    const amount = sum(committed.map((c) => Math.max(0, (top ? c : Math.min(c, level)) - prev)));
    pots.push({ amount, eligible: committed.flatMap((c, i) => (live[i] && c >= level ? [i] : [])) });
    prev = level;
  });
  return pots.filter((p) => p.amount > 0);
}

/** Pick who acts next, or close the street (dealing on, running out the board, or paying the pot). */
function settle(s: HoldemState, from: Seat): HoldemState {
  if (inHand(s).filter(Boolean).length === 1) return award(s, false);
  const n = s.seated.length;
  if (s.toAct !== null && s.toAct !== from && pending(s, s.toAct)) return s;
  for (let k = 1; k <= n; k++) {
    const seat = (from + k) % n;
    if (pending(s, seat)) return { ...s, toAct: seat };
  }
  if (s.street === 'river') return award({ ...s, toAct: null }, true);
  const zeros = s.seated.map(() => 0);
  return settle({ ...dealStreet(s), bets: zeros, currentBet: 0, minRaise: s.options.bigBlind, acted: s.seated.map(() => false), toAct: null }, s.button);
}

/** Pays every pot to its best eligible hand(s); ties split with odd chips going left of the button first. */
function award(s: HoldemState, showdown: boolean): HoldemState {
  const live = inHand(s);
  const shown = showdown ? live.flatMap((x, seat) => (x ? [{ seat, hand: bestHand([...(s.hole[seat] as Card[]), ...s.board]) }] : [])) : [];
  const pots = sidePots(s.committed, live);
  const payouts = s.seated.map(() => 0);
  const n = s.seated.length;
  for (const pot of pots) {
    let winners = pot.eligible;
    if (showdown) {
      const entries = shown.filter((e) => pot.eligible.includes(e.seat));
      const top = entries.reduce((best, e) => (compareScores(e.hand.score, best) > 0 ? e.hand.score : best), [-1] as readonly number[]);
      winners = entries.filter((e) => compareScores(e.hand.score, top) === 0).map((e) => e.seat);
    }
    const order = [...winners].sort((a, b) => ((a - s.button - 1 + n) % n) - ((b - s.button - 1 + n) % n));
    const share = Math.floor(pot.amount / order.length);
    let odd = pot.amount - share * order.length;
    for (const w of order) {
      payouts[w] = (payouts[w] as number) + share + (odd-- > 0 ? 1 : 0);
    }
  }
  const stacks = s.stacks.map((c, i) => c + (payouts[i] as number));
  const winners = payouts.flatMap((p, i) => (p > 0 ? [i] : []));
  const seated = s.seated.map((x, i) => x && (stacks[i] as number) > 0);
  const left = seated.flatMap((x, i) => (x ? [i] : []));
  const zeros = s.seated.map(() => 0);
  return {
    ...s,
    stacks,
    seated,
    committed: zeros,
    bets: zeros,
    currentBet: 0,
    toAct: null,
    street: 'showdown',
    hand: { winners, payouts, pots, shown },
    last: { kind: 'won', winners, byFold: !showdown },
    result: left.length <= 1 ? { winners: left, draw: false, reason: 'all-chips' } : null,
  };
}

/** Burns a card and turns the flop, turn or river. */
function dealStreet(s: HoldemState): HoldemState {
  const deck = [...s.deck];
  if (s.street === 'preflop') return { ...s, burned: [...s.burned, deck.shift() as Card], board: deck.splice(0, 3), deck, street: 'flop', last: { kind: 'street', street: 'flop' } };
  const street: Street = s.street === 'flop' ? 'turn' : 'river';
  return { ...s, burned: [...s.burned, deck.shift() as Card], board: [...s.board, deck.shift() as Card], deck, street, last: { kind: 'street', street } };
}

function finishHand(s: HoldemState, byFold: boolean): HoldemState {
  if (s.options.betting) return award(s, !byFold);
  const live = inHand(s);
  if (byFold) {
    const winners = live.flatMap((x, i) => (x ? [i] : []));
    return { ...s, street: 'showdown', hand: { winners, shown: [] }, last: { kind: 'won', winners, byFold: true } };
  }
  const shown = live.flatMap((x, seat) => (x ? [{ seat, hand: bestHand([...(s.hole[seat] as Card[]), ...s.board]) }] : []));
  const top = shown.reduce((best, e) => (compareScores(e.hand.score, best) > 0 ? e.hand.score : best), [-1] as readonly number[]);
  const winners = shown.filter((e) => compareScores(e.hand.score, top) === 0).map((e) => e.seat);
  return { ...s, street: 'showdown', hand: { winners, shown }, last: { kind: 'won', winners, byFold: false } };
}

const BETTING_RULES = [
  'Texas Hold’em with chips. Everyone starts with the same stack (a setting, 1000 by default). The app is the dealer and the bank: it deals, posts the blinds, takes bets and pays the pot.',
  'Each player gets 2 private cards. Five shared cards go on the board: 3 (the flop), then 1 (the turn), then 1 (the river). Make the best five-card hand from your two cards and the board.',
  'The button moves one seat each hand. The seat left of the button posts the small blind (half the big blind) and the next seat posts the big blind, both automatically. Heads-up, the button is the small blind and acts first before the flop.',
  'Betting is no-limit. On your turn: Check (nothing to call), Call, Fold, Bet or Raise to any total from the minimum up to your whole stack, or go All-in. A raise must be at least as big as the last bet or raise (at least the big blind).',
  'A short all-in raise that is smaller than a full raise does not let players who already acted raise again; they may only call or fold.',
  'A betting round ends when everyone still in has matched the bet and had a turn. The next card is then dealt automatically. If everyone else folds, the last player wins the pot without showing cards.',
  'If you are all-in, you can only win as much from each player as you put in: bigger bets go into side pots that only players who covered them can win. When only all-in players (or just one player with chips) are left, the app deals out the rest of the board.',
  'At the showdown every player still in turns their cards over and the app pays each pot to the best hand. Ties split the pot; an odd chip goes to the first tied player left of the button.',
  'After the showdown any player taps Next to start the next hand. Players with no chips left are out. The session ends when one player has all the chips.',
  'Leave to quit: you fold and your chips leave with you. Chips you already bet stay in the pot. Not in the app: antes, straddles, rebuys, running it twice, or the exact rule that a run of short all-ins can add up to a full raise.',
];

const OFF_RULES =
  'the app only deals. It tracks the button and blinds (no amounts), the dealer taps Next for each street and the showdown, players tap Fold, and chips, bets and pots are settled at the table. Ties split the pot; the session ends when one player is left sitting.';

/** Smallest total a seat may bet or raise to, and the most it can (all its chips). */
export function betLimits(s: HoldemState, seat: Seat): { readonly min: number; readonly max: number } {
  const max = (s.bets[seat] as number) + (s.stacks[seat] as number);
  return { min: Math.min(max, s.currentBet + s.minRaise), max };
}

const allSeated = (s: HoldemState) => s.seated.flatMap((x, i) => (x ? [i] : []));

export const holdem: GameDefinition<HoldemState, HoldemAction, HoldemView, HoldemOptions> = {
  id: 'holdem',
  name: "Texas Hold'em",
  rules: [...BETTING_RULES, `Setting: Betting. Off turns all of the above betting off: ${OFF_RULES}`],
  minSeats: 2,
  maxSeats: 10,
  defaultOptions: { betting: true, stack: 1000, bigBlind: 20 },
  optionSpecs: [
    {
      key: 'betting',
      label: 'Betting',
      help: 'On: the app keeps chip stacks, blinds, bets and pots. Off: the app only deals and you play for real money.',
      choices: [
        { value: true, label: 'Chips in the app' },
        { value: false, label: 'Off (just deal)' },
      ],
    },
    {
      key: 'stack',
      label: 'Starting chips',
      choices: [500, 1000, 2000, 5000].map((value) => ({ value, label: `${value} chips` })),
    },
    {
      key: 'bigBlind',
      label: 'Big blind',
      help: 'The small blind is half of it, and it is the smallest bet.',
      choices: [10, 20, 50, 100].map((value) => ({ value, label: `${value}` })),
    },
  ],

  setup({ seats, options, rng }) {
    // The first button is drawn at random, as with a high-card draw.
    const button = rng.int(seats);
    const base = {
      options,
      seated: Array.from({ length: seats }, () => true),
      stacks: Array.from({ length: seats }, () => options.stack),
      button,
      handNo: 1,
      last: { kind: 'deal', hand: 1, button } as HoldemEvent,
      result: null,
    };
    return dealHand(base, shuffle(standardDeck(), rng));
  },

  // Betting off: the dealer deals; anyone still sitting may fold (if in the hand) or leave.
  // Betting on: the player to act comes first; every seated player may leave, and at the showdown anyone may deal on.
  activeSeats(s) {
    if (s.result) return [];
    if (!s.options.betting) return [s.button, ...s.seated.flatMap((x, i) => (x && i !== s.button ? [i] : []))];
    return s.toAct === null ? allSeated(s) : [s.toAct, ...allSeated(s).filter((i) => i !== s.toAct)];
  },

  // Only the player to act (or the dealer, without chips) is due; others may still fold or leave.
  waitingOn(s) {
    if (s.result) return [];
    if (!s.options.betting) return [s.button];
    return s.toAct === null ? allSeated(s) : [s.toAct];
  },

  validate(s, a, seat) {
    if (!s.seated[seat]) return { ok: false, reason: 'you have left the table' };
    if (a.type === 'leave') return { ok: true };
    if (!s.options.betting) {
      switch (a.type) {
        case 'next':
          return seat === s.button ? { ok: true } : { ok: false, reason: 'only the dealer deals' };
        case 'fold':
          if (s.street === 'showdown') return { ok: false, reason: 'the hand is over' };
          return inHand(s)[seat] ? { ok: true } : { ok: false, reason: 'you are not in this hand' };
        default:
          return { ok: false, reason: 'unknown action' };
      }
    }
    if (a.type === 'next') return s.street === 'showdown' ? { ok: true } : { ok: false, reason: 'the hand is not over' };
    if (!['fold', 'check', 'call', 'bet', 'allin'].includes(a.type)) return { ok: false, reason: 'unknown action' };
    if (s.street === 'showdown') return { ok: false, reason: 'the hand is over' };
    if (seat !== s.toAct) return { ok: false, reason: 'not your turn' };
    const owe = s.currentBet - (s.bets[seat] as number);
    const { min, max } = betLimits(s, seat);
    switch (a.type) {
      case 'fold':
        return { ok: true };
      case 'check':
        return owe === 0 ? { ok: true } : { ok: false, reason: `you must call ${owe} or fold` };
      case 'call':
        return owe > 0 ? { ok: true } : { ok: false, reason: 'nothing to call' };
      case 'bet': {
        if (!Number.isInteger(a.to)) return { ok: false, reason: 'bad amount' };
        if (a.to > max) return { ok: false, reason: `you only have ${max - (s.bets[seat] as number)} chips` };
        if (a.to <= s.currentBet) return { ok: false, reason: 'a raise must be higher than the current bet' };
        if (s.acted[seat]) return { ok: false, reason: 'betting was not reopened: call or fold' };
        return a.to >= min || a.to === max ? { ok: true } : { ok: false, reason: `the smallest raise is to ${min}` };
      }
      default:
        // all-in
        return max > s.currentBet && s.acted[seat] ? { ok: false, reason: 'betting was not reopened: call or fold' } : { ok: true };
    }
  },

  apply(s, a, { actor, rng }) {
    const seat = actor as Seat;
    if (a.type === 'fold') {
      const next = { ...s, folded: s.folded.map((f, i) => f || i === seat), last: { kind: 'fold', seat } as HoldemEvent };
      if (s.options.betting) return settle(next, seat);
      return inHand(next).filter(Boolean).length === 1 ? finishHand(next, true) : next;
    }
    if (a.type === 'leave') {
      const seated = s.seated.map((x, i) => x && i !== seat);
      const left = seated.flatMap((x, i) => (x ? [i] : []));
      const next: HoldemState = { ...s, seated, folded: s.folded.map((f, i) => f || i === seat), last: { kind: 'leave', seat } };
      if (left.length === 1) return { ...next, toAct: null, result: { winners: left, draw: false, reason: 'last-seated' } };
      // A departing dealer passes the button on so someone can keep dealing.
      const moved = seat === s.button ? { ...next, button: nextSeated(seated, seat) } : next;
      if (s.street === 'showdown') return moved;
      if (s.options.betting) return settle(moved, seat);
      return inHand(moved).filter(Boolean).length === 1 ? finishHand(moved, true) : moved;
    }
    if (s.options.betting) {
      if (a.type === 'next') {
        const button = nextSeated(s.seated, s.button);
        const handNo = s.handNo + 1;
        return dealHand({ ...s, button, handNo, last: { kind: 'deal', hand: handNo, button } }, shuffle(standardDeck(), rng));
      }
      const stack = s.stacks[seat] as number;
      const owe = s.currentBet - (s.bets[seat] as number);
      const { max } = betLimits(s, seat);
      let t: HoldemState;
      if (a.type === 'check') {
        t = { ...s, last: { kind: 'check', seat } };
      } else if (a.type === 'call' || (a.type === 'allin' && max <= s.currentBet)) {
        const amount = Math.min(owe, stack);
        t = { ...put(s, seat, amount), last: { kind: 'call', seat, amount } };
      } else {
        const to = a.type === 'bet' ? a.to : max;
        const full = to - s.currentBet >= s.minRaise;
        const raised = put(s, seat, to - (s.bets[seat] as number));
        t = {
          ...raised,
          currentBet: to,
          minRaise: full ? to - s.currentBet : s.minRaise,
          acted: full ? s.acted.map(() => false) : s.acted,
          last: { kind: 'raise', seat, to, allIn: to === max },
        };
      }
      return settle({ ...t, acted: set(t.acted, seat, true) }, seat);
    }
    // next: deal the next street (burning one card first), score the showdown, or start a new hand.
    switch (s.street) {
      case 'preflop':
      case 'flop':
      case 'turn':
        return dealStreet(s);
      case 'river':
        return finishHand(s, false);
      case 'showdown': {
        const button = nextSeated(s.seated, s.button);
        const handNo = s.handNo + 1;
        return dealHand({ ...s, button, handNo, last: { kind: 'deal', hand: handNo, button } }, shuffle(standardDeck(), rng));
      }
    }
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const live = inHand(s);
    const revealed = new Set(s.hand?.shown.map((e) => e.seat) ?? []);
    const myTurn = you !== null && s.options.betting && s.toAct === you;
    const limits = myTurn ? betLimits(s, you) : { min: 0, max: 0 };
    return {
      betting: s.options.betting,
      bigBlind: s.options.bigBlind,
      stacks: s.stacks,
      bets: s.bets,
      committed: s.committed,
      pot: sum(s.committed),
      pots: sidePots(s.committed, live),
      toAct: s.toAct,
      toCall: myTurn ? Math.min(s.currentBet - (s.bets[you] as number), s.stacks[you] as number) : 0,
      minRaiseTo: myTurn && !s.acted[you] && limits.max > s.currentBet ? limits.min : 0,
      maxTo: limits.max,
      currentBet: s.currentBet,
      you,
      hole: you === null ? null : (s.hole[you] ?? null),
      shown: s.hole.map((h, i) => (revealed.has(i) ? h : null)),
      board: s.board,
      street: s.street,
      handNo: s.handNo,
      positions: positions(s),
      seated: s.seated,
      folded: s.folded,
      inHand: live,
      hand: s.hand,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || !s.seated[seat]) return [];
    const out: HoldemAction[] = [];
    if (!s.options.betting) {
      if (seat === s.button) out.push({ type: 'next' }, { type: 'next' }, { type: 'next' });
      if (s.street !== 'showdown' && inHand(s)[seat]) out.push({ type: 'fold' });
    } else if (s.street === 'showdown') {
      out.push({ type: 'next' }, { type: 'next' });
    } else if (seat === s.toAct) {
      const owe = s.currentBet - (s.bets[seat] as number);
      out.push({ type: 'fold' }, owe > 0 ? { type: 'call' } : { type: 'check' });
      if (holdem.validate(s, { type: 'allin' }, seat).ok) out.push({ type: 'allin' });
      const { min, max } = betLimits(s, seat);
      const potRaise = s.currentBet + sum(s.committed) + owe;
      for (const to of new Set([min, Math.min(max, potRaise)])) {
        const a: HoldemAction = { type: 'bet', to };
        if (holdem.validate(s, a, seat).ok) out.push(a);
      }
    }
    out.push({ type: 'leave' });
    return out;
  },
};
